import React, { useState, useEffect } from 'react'
import {
	Box,
	Typography,
	Card,
	Grid,
	Button,
	Chip,
	Paper,
	IconButton,
	Divider,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
} from '@mui/material'
import { styled } from '@mui/system'
import { motion, AnimatePresence } from 'framer-motion'
import EventSeatIcon from '@mui/icons-material/EventSeat'
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { ArrowRightLeft, DollarSign } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../config/api'
import { filter, flatten, find } from 'lodash'
import OptimizedSeatLayout from '../../components/OptimizedSeatLayout'
import { getSeatConfig, isSeatCountSupported, getSupportedSeatCounts } from '../../components/seatConfigs'

const AnimatedButton = motion(Button)

const seatIconsSmall = {
	'first-class': '/images/tealseatsmall.svg',
	premium: '/images/ornageseatsmall.svg',
	economy: '/images/blueseatsmall.svg',
	reserved: '/images/reservedseatsmall.svg',
	driver: '/images/steeringwheel.svg',
}

const Container = styled(Box)({
	maxWidth: '800px',
	margin: '0 auto',
	'& .MuiCard-root': {
		width: '344px',
	},
})

const LegendItem = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	marginBottom: '12px',
})

const SeatContainer = styled(Box)({
	padding: '24px',
	backgroundColor: '#fff',
})

const SelectedSeatsTable = styled(Box)({
	// Styles for selected seats table
})

const SeatIconSmall = ({ type, isReserved }) => {
	const iconPath = isReserved ? seatIconsSmall.reserved : seatIconsSmall[type]

	return (
		<img
			src={iconPath}
			alt={`${type} seat`}
			style={{ width: '18px', height: '18px' }}
		/>
	)
}

const SeatSelection = ({
	trip,
	vehicleType = 'matatu',
	onBookingComplete,
	progressSeats,
}) => {
	const navigate = useNavigate()
	const [selectedSeats, setSelectedSeats] = useState([])
	const [seatLayout, setSeatLayout] = useState([])
	const [loading, setLoading] = useState(true)

	// Generate seat layout based on vehicle type
	// useEffect(() => {
	// 	const seatCounts = {
	// 		matatu: 14,
	// 		minibus: 25,
	// 		bus: 33,
	// 	}

	// 	const generateSeats = count => {
	// 		return Array.from({ length: count }, (_, index) => ({
	// 			id: index + 1,
	// 			category: getRandomCategory(), // In real app, this would come from backend
	// 			isReserved: Math.random() > 0.8, // Simulated reserved seats
	// 			price: getPriceByCategory(getRandomCategory()),
	// 		}))
	// 	}

	// 	setSeatLayout(generateSeats(seatCounts[vehicleType]))
	// }, [vehicleType])

	useEffect(() => {
		const fetchSeats = async () => {
			try {
				const url = `bookingMobile/seats?tripId=${trip._id}`
				const response = await api.get(url)
				if (!response) {
					throw new Error('Failed to fetch seats')
				}

				// Transform API data to match our component's needs
				const transformedSeats = response.data.data
					.map(seat => ({
						id: seat.seatNo,
						category:
							seat.seatType === 'economyClass'
								? 'economy'
								: seat.seatType === 'firstClass'
								? 'first-class'
								: 'premium',
						isReserved: seat.booked || seat.payedFor,
						price: Number(seat.price),
						_id: seat._id, // Keep the original ID for API calls
						...seat,
					}))
					.sort((a, b) => a.id - b.id) // Sort by seatNo

				setSeatLayout(transformedSeats)
				setLoading(false)
			} catch (error) {
				console.error('Error fetching seats:', error)
				setLoading(false)
			}
		}

		if (trip._id) {
			fetchSeats()
		}
	}, [trip._id])

	useEffect(() => {
		if (Array.isArray(progressSeats) && progressSeats.length > 0) {
			setSelectedSeats(progressSeats)
		}
	}, [progressSeats])

	const handleSeatClick = seat => {
		if (seat.isReserved) return

		const isSelected = selectedSeats.find(s => s.id === seat.id)
		if (isSelected) {
			setSelectedSeats(selectedSeats.filter(s => s.id !== seat.id))
		} else {
			setSelectedSeats([...selectedSeats, seat])
		}
	}

	const handleSeatBooking = async selectedSeats => {
		try {
			// Extract seat IDs
			const selectedSeatIds = selectedSeats.map(seat => seat._id)

			// Make the API call
			const response = await api.put('bookingMobile/seats/select', {
				data: selectedSeatIds,
			})

			// Ensure response is valid
			if (response?.data.seats) {
				// Populate the returned seats with details from selectedSeats
				const populatedSeats = response.data.seats
					.map(seatObj => {
						// Extract the seat ID and its value (true/false)
						const [seatId, isAvailable] = Object.entries(seatObj)[0]

						// Find the corresponding seat in the selectedSeats array
						const matchingSeat = selectedSeats.find(
							seat => seat._id === seatId && isAvailable,
						)

						// Merge the seat data with the isAvailable property
						return matchingSeat ? { ...matchingSeat, isAvailable } : null
					})
					.filter(Boolean) // Remove null values in case no match was found

				// Update state
				setSelectedSeats(populatedSeats)

				// Navigate to booking details with populated seats
				navigate('/app/booking/details', {
					state: { trip, selectedSeats: populatedSeats },
				})
			} else {
				console.error('No seats returned from API')
				// Optionally notify the user about this issue
			}
		} catch (error) {
			console.error('Error during seat booking:', error)
			// Optionally notify the user of the error
		}
	}

	const getTotalCost = () => {
		return selectedSeats.reduce((total, seat) => total + Number(seat.price), 0)
	}

	const renderSeats = () => {
		// Check if seat count is supported
		if (!isSeatCountSupported(seatLayout.length)) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography variant="h6" color="error" gutterBottom>
						Unsupported Vehicle Configuration
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						This vehicle has {seatLayout.length} seats. Currently supported configurations are:
					</Typography>
					<Typography variant="body2" color="primary">
						{getSupportedSeatCounts().join(', ')} seaters
					</Typography>
				</Box>
			)
		}

		return (
			<OptimizedSeatLayout
				seatData={seatLayout}
				selectedSeats={selectedSeats}
				onSeatClick={handleSeatClick}
			/>
		)
	}

	const buttonVariants = {
		hover: {
			scale: 1.05,
			transition: { duration: 0.2 },
		},
		tap: {
			scale: 0.95,
		},
	}

	return (
		<Container>
			<Grid container spacing={3} justifyContent="center">
				<Grid item xs={12} md={6}>
					{/* Legend Card */}
					<Card sx={{ mb: 3 }}>
						<Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							{/* <Typography variant="h6" gutterBottom>
								Seat Categories
							</Typography> */}
							<LegendItem>
								<SeatIconSmall type="first-class" />
								<Typography sx={{ fontWeight: '600' }} variant="caption">
									First Class
								</Typography>
							</LegendItem>
							<LegendItem>
								<SeatIconSmall type="premium" />
								<Typography sx={{ fontWeight: '600' }} variant="caption">
									Premium
								</Typography>
							</LegendItem>
							<LegendItem>
								<SeatIconSmall type="economy" />
								<Typography sx={{ fontWeight: '600' }} variant="caption">
									Economy
								</Typography>
							</LegendItem>
							<LegendItem>
								<SeatIconSmall type="reserved" isReserved />
								<Typography sx={{ fontWeight: '600' }} variant="caption">
									Reserved
								</Typography>
							</LegendItem>
							<LegendItem>
								<Box
									sx={{
										width: '24px',
										height: '24px',
										border: '2px solid #ef404f',
										borderRadius: '6px',
									}}
								></Box>
								<Typography sx={{ fontWeight: '600' }} variant="caption">
									Seleced
								</Typography>
							</LegendItem>
						</Box>
					</Card>

					{/* Seat Selection Container */}
					<SeatContainer>
						{loading ? (
							<Box sx={{ textAlign: 'center', py: 6 }}>
								<Typography variant="h6" color="textSecondary" gutterBottom>
									Loading seat layout...
								</Typography>
								<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
									{/* Simple loading skeleton */}
									{Array.from({ length: 12 }).map((_, index) => (
										<Box
											key={index}
											sx={{
												width: '32px',
												height: '32px',
												bgcolor: '#f0f0f0',
												borderRadius: '6px',
												margin: '4px',
												animation: 'pulse 1.5s ease-in-out infinite',
												animationDelay: `${index * 0.1}s`,
												'@keyframes pulse': {
													'0%': { opacity: 0.6 },
													'50%': { opacity: 1 },
													'100%': { opacity: 0.6 }
												}
											}}
										/>
									))}
								</Box>
							</Box>
						) : (
							renderSeats()
						)}

						{/* <Box sx={{ textAlign: 'end', mt: 3 }}>
							<AnimatedButton
								onClick={() => onBookingComplete([])}
								variant="contained"
								color="primary"
								variants={buttonVariants}
								whileHover="hover"
								whileTap="tap"
							>
								Skip seat selection
							</AnimatedButton>
						</Box> */}
					</SeatContainer>
				</Grid>

				{/* Right Column - Booking Summary */}
				<Grid item xs={12} md={6}>
					<Card>
						<Box sx={{ p: 3 }}>
							{/* <Typography variant="h6" gutterBottom>
								Trip Details
							</Typography> */}

							<Box sx={{}}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
								>
									<Typography sx={{ fontWeight: '600' }} variant="body1">
										{trip?.departure}
									</Typography>
									<IconButton
										type="button"
										// onClick={handleSwap}
										aria-label="Swap locations"
										sx={{
											color: 'secondary.main',
											flex: '0 0 auto',
											alignSelf: 'center',
										}}
									>
										<ArrowRightLeft size={18} style={{ lineHeight: 1 }} />
									</IconButton>
									<Typography sx={{ fontWeight: '600' }} variant="body1">
										{trip?.destination}
									</Typography>
								</Box>
								{/* <Typography variant="caption" color="textSecondary">
									Departure: 10:00 AM
								</Typography> */}
							</Box>

							{/* <Divider /> */}

							<SelectedSeatsTable>
								{/* <Typography variant="subtitle1" gutterBottom>
									Selected Seats
								</Typography> */}
								{/* {selectedSeats.map(seat => (
									<Box key={seat.id} sx={{ mb: 2 }}>
										<Typography variant="body2">
											Seat {seat.id} - {seat.category}
											<Typography component="span" sx={{ float: 'right' }}>
												KES {seat.price.toLocaleString()}
											</Typography>
										</Typography>
									</Box>
								))} */}

								{/* <TableContainer component={Paper}> */}
								<Table>
									<TableHead>
										<TableRow>
											<TableCell
												sx={{
													fontSize: '11px',
													color: '#7E7C85',
													padding: '4px 16px',
												}}
											>
												Seat Number
											</TableCell>
											<TableCell
												sx={{
													fontSize: '11px',
													color: '#7E7C85',
													padding: '4px 16px',
												}}
											>
												Category
											</TableCell>
											<TableCell
												sx={{
													fontSize: '11px',
													color: '#7E7C85',
													padding: '4px 16px',
												}}
												align="right"
											>
												Price (KES)
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{selectedSeats.map(seat => (
											<TableRow key={seat.id}>
												<TableCell
													sx={{ fontSize: '11px', padding: '8px 16px' }}
												>
													Seat {seat.id}
												</TableCell>
												<TableCell
													sx={{ fontSize: '11px', padding: '8px 16px' }}
												>
													{seat.category}
												</TableCell>
												<TableCell
													sx={{ fontSize: '11px', padding: '8px 16px' }}
													align="right"
												>
													{seat.price.toLocaleString()}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								{/* </TableContainer> */}
							</SelectedSeatsTable>

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									mb: 3,
								}}
							>
								{/* <Typography variant="subtitle1" ></Typography> */}
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									{/* <DollarSign size={20} /> */}
									<img src="/images/cash.svg" alt="money" style={{}} />

									<Box>
										<Typography
											sx={{
												color: 'grey',
												margin: '0 !important',
												fontSize: '10px',
											}}
											variant="body1"
										>
											Total Fare
										</Typography>

										<Typography
											sx={{
												color: 'green',
												margin: '0 !important',
												fontSize: '12px',
												fontWeight: '600',
											}}
											variant="body1"
										>
											KES {getTotalCost().toLocaleString()}
										</Typography>
									</Box>
								</Box>
							</Box>

							<Button
								variant="contained"
								fullWidth
								disabled={selectedSeats.length === 0}
								onClick={() => {
									// onBookingComplete(selectedSeats)

									handleSeatBooking(selectedSeats)
								}}
							>
								Book Now
							</Button>
						</Box>
					</Card>
				</Grid>
			</Grid>
		</Container>
	)
}

export default SeatSelection
